<template>
  <b-modal
    ref="modal"
    id="forms-update-reminder-settings"
    size="lg"
    scrollable
    title="Medical History Update Reminders"
    body-class="bg-white"
    hide-header-close
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner small /> Loading settings…
    </div>

    <div v-else class="fr-body">
      <p class="fr-intro">
        Automatically remind patients to update their medical history. The reminder rides along with
        the patient's next appointment confirmation — so it lands when they actually have a visit
        coming up and are most likely to act.
      </p>

      <label class="fr-switch">
        <input type="checkbox" v-model="activated" />
        <span>Enable medical-history update reminders</span>
      </label>

      <fieldset :disabled="!activated" :class="{ 'fr-disabled': !activated }">
        <!-- Interval -->
        <div class="fr-field">
          <label class="fr-label">How often should patients update their medical history?</label>
          <div class="sg-input-row">
            <input
              type="number" min="1" max="99" maxlength="2"
              class="sg-input sg-input-num"
              v-model.number="intervalNumber"
              @input="clampInterval"
            />
            <select class="sg-input fr-unit" v-model="intervalUnit">
              <option value="months">month(s)</option>
              <option value="years">year(s)</option>
            </select>
          </div>
          <p class="sg-hint">e.g. 18 months, or 2 years. Whole numbers only (max 2 digits).</p>
        </div>

        <!-- Channels -->
        <div class="fr-field">
          <label class="fr-label">How should we notify them?</label>
          <div class="fr-checks">
            <label><input type="checkbox" v-model="channelSms" /> SMS</label>
            <label><input type="checkbox" v-model="channelEmail" /> Email</label>
            <label><input type="checkbox" :checked="channelSms && channelEmail" @change="toggleBoth" /> Both</label>
          </div>
        </div>

        <!-- SMS template -->
        <div class="fr-field" v-if="channelSms">
          <label class="fr-label">Wiki SMS Template <span class="fr-req">*</span></label>
          <p class="sg-hint">Sent via SMS when patients are due. Shows wiki pages tagged “form” and “sms-template”.</p>
          <select class="sg-input" v-model="smsTemplateId">
            <option :value="null">— Select a template —</option>
            <option v-for="t in smsOptions" :key="t.WikiPageID" :value="t.WikiPageID">{{ t.Name }}</option>
          </select>
          <p v-if="formSmsTemplates.length === 0" class="fr-warn">
            No wiki templates tagged “form” + “sms-template” yet. Create one in the Wiki and tag it accordingly.
          </p>
        </div>

        <!-- Email template -->
        <div class="fr-field" v-if="channelEmail">
          <label class="fr-label">Wiki Email Template <span class="fr-req">*</span></label>
          <p class="sg-hint">Sent via Email when patients are due. Shows wiki pages tagged “form” and “email-template”.</p>
          <select class="sg-input" v-model="emailTemplateId">
            <option :value="null">— Select a template —</option>
            <option v-for="t in emailOptions" :key="t.WikiPageID" :value="t.WikiPageID">{{ t.Name }}</option>
          </select>
          <p v-if="formEmailTemplates.length === 0" class="fr-warn">
            No email-ready wiki templates tagged “form” + “email-template” yet (an email template also needs a SendGrid template configured).
          </p>
        </div>

        <!-- Trigger forms -->
        <div class="fr-field">
          <label class="fr-label">Form(s) that start the update clock <span class="fr-req">*</span></label>
          <p class="sg-hint">
            When a patient fills any of these (e.g. a New Patient form, or a Medical History Update form),
            we begin tracking when they're next due — and re-arm it each time they re-submit.
          </p>
          <multiselect
            v-model="triggerForms"
            :options="forms"
            :multiple="true"
            :close-on-select="false"
            track-by="FormIDForWeb"
            label="Name"
            placeholder="Select form(s)"
          />
        </div>

        <p class="fr-explain">
          When a due patient gets their next appointment confirmation (or, if confirmations are off,
          their next reminder), we'll automatically include this update request.
        </p>
      </fieldset>
    </div>

    <template #modal-footer>
      <b-button variant="light" @click="hide">Cancel</b-button>
      <b-button variant="success" :disabled="saving" @click="save">
        <b-spinner v-if="saving" small /> Save Settings
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  name: 'FormsUpdateReminderSettings',
  components: { Multiselect },
  props: {
    siteId: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      saving: false,
      activated: false,
      intervalNumber: 18,
      intervalUnit: 'months',
      channelSms: false,
      channelEmail: false,
      smsTemplateId: null,
      emailTemplateId: null,
      // The slots as loaded. A plain-message slot (WikiPageID 'inline:medhx:…', set in Forms Settings) is not a wiki page,
      // so it is offered as its own option and saved back UNCHANGED while it stays selected — without this, Save here
      // wrote smsTemplate: null and silently dropped the message (Forms Settings plan, WP3 review 2026-10-20).
      loadedSmsSlot: null,
      loadedEmailSlot: null,
      triggerForms: [],
      forms: [],
      wikiPages: [],
    };
  },
  computed: {
    smsOptions() {
      return this.inlineOption(this.loadedSmsSlot).concat(this.formSmsTemplates);
    },
    emailOptions() {
      return this.inlineOption(this.loadedEmailSlot).concat(this.formEmailTemplates);
    },
    formSmsTemplates() {
      return this.wikiPages.filter(o => this.hasTag(o, 'form') && this.hasTag(o, 'sms-template'));
    },
    formEmailTemplates() {
      return this.wikiPages.filter(o => this.hasTag(o, 'form') && this.hasTag(o, 'email-template') && o.SendgridTemplateID);
    },
  },
  methods: {
    isInlineSlot(slot) {
      return !!slot && typeof slot.WikiPageID === 'string' && slot.WikiPageID.indexOf('inline:') === 0;
    },
    inlineOption(slot) {
      return this.isInlineSlot(slot) ? [{ WikiPageID: slot.WikiPageID, Name: 'Plain message (edit in Forms Settings)' }] : [];
    },
    // The slot to save for a channel: the loaded plain-message slot as is while it is still selected, else the picked wiki page.
    slotToSave(id, templates, loaded) {
      if (this.isInlineSlot(loaded) && id === loaded.WikiPageID) return JSON.parse(JSON.stringify(loaded));
      const t = templates.find(x => x.WikiPageID === id);
      return t ? { WikiPageID: t.WikiPageID, Name: t.Name } : null;
    },
    // Exact (comma-split) tag match — avoids 'form' spuriously matching 'platform'/'form-archived'.
    hasTag(o, tag) {
      if (!o || !o.Tags) return false;
      return String(o.Tags).split(',').map(t => t.trim()).includes(tag);
    },
    clampInterval() {
      let v = parseInt(this.intervalNumber, 10);
      if (!Number.isFinite(v) || v < 1) { this.intervalNumber = ''; return; }
      this.intervalNumber = Math.min(v, 99);
    },
    toggleBoth(e) {
      const checked = e.target.checked;
      this.channelSms = checked;
      this.channelEmail = checked;
    },
    async open() {
      this.$refs.modal.show();
      this.loading = true;
      try {
        const [settingsRes, wikiRes] = await Promise.all([
          this.$store.dispatch('forms/getFormsReminderSettings', { siteID: this.siteId }),
          this.$store.dispatch('wiki/getWikiPages', { siteID: this.siteId }),
        ]);
        this.forms = (settingsRes && settingsRes.data && settingsRes.data.forms) || [];
        this.wikiPages = (wikiRes && wikiRes[0]) || [];
        this.applySettings((settingsRes && settingsRes.data && settingsRes.data.settings) || {});
      } catch (e) {
        console.error('Failed to load forms reminder settings', e);
      } finally {
        this.loading = false;
      }
    },
    applySettings(s) {
      this.activated = s.activated === true;
      this.intervalNumber = s.intervalNumber || 18;
      this.intervalUnit = s.intervalUnit === 'years' ? 'years' : 'months';
      const ch = s.channels || {};
      this.channelSms = !!ch.sms;
      this.channelEmail = !!ch.email;
      this.smsTemplateId = (s.smsTemplate && s.smsTemplate.WikiPageID) || null;
      this.emailTemplateId = (s.emailTemplate && s.emailTemplate.WikiPageID) || null;
      this.loadedSmsSlot = s.smsTemplate || null;
      this.loadedEmailSlot = s.emailTemplate || null;
      this.triggerForms = Array.isArray(s.triggerForms)
        ? s.triggerForms.map(f => ({ FormIDForWeb: f.FormIDForWeb, Name: f.Name }))
        : [];
    },
    hide() { this.$refs.modal.hide(); },
    buildSettings() {
      const smsSlot = this.slotToSave(this.smsTemplateId, this.formSmsTemplates, this.loadedSmsSlot);
      const emailSlot = this.slotToSave(this.emailTemplateId, this.formEmailTemplates, this.loadedEmailSlot);
      return {
        activated: this.activated,
        intervalNumber: this.intervalNumber,
        intervalUnit: this.intervalUnit,
        channels: { sms: this.channelSms, email: this.channelEmail },
        smsTemplate: this.channelSms ? smsSlot : null,
        emailTemplate: this.channelEmail ? emailSlot : null,
        triggerForms: (this.triggerForms || []).map(f => ({ FormIDForWeb: f.FormIDForWeb, Name: f.Name })),
      };
    },
    validate() {
      if (!this.activated) return null; // saving a disabled config is allowed
      const errs = [];
      const n = parseInt(this.intervalNumber, 10);
      if (!Number.isFinite(n) || n < 1 || n > 99) errs.push('• Enter a valid update interval (1–99).');
      if (!this.channelSms && !this.channelEmail) errs.push('• Select at least one channel (SMS, Email, or Both).');
      if (this.channelSms && !this.smsTemplateId) errs.push('• Select a Wiki SMS template.');
      if (this.channelEmail && !this.emailTemplateId) errs.push('• Select a Wiki Email template.');
      if (!this.triggerForms || this.triggerForms.length === 0) errs.push('• Select at least one trigger form.');
      return errs.length ? errs : null;
    },
    async save() {
      const errs = this.validate();
      if (errs) {
        this.$bvModal.msgBoxOk(errs.join('\n'), { title: 'Please complete the settings' });
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('forms/saveFormsReminderSettings', {
          siteID: this.siteId,
          settings: this.buildSettings(),
        });
        this.$bvToast.toast('Medical-history update reminder settings saved.', {
          title: 'Saved', variant: 'success', solid: true,
        });
        this.hide();
      } catch (e) {
        this.$bvModal.msgBoxOk('Could not save settings. Please try again.', { title: 'Save failed' });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.fr-intro { font-size: 13px; color: #475569; margin-bottom: 14px; line-height: 1.5; }
.fr-switch { display: flex; align-items: center; gap: 8px; font-weight: 600; color: #0f172a; margin-bottom: 6px; cursor: pointer; }
.fr-disabled { opacity: 0.55; }
.fr-field { margin-top: 16px; }
.fr-label { font-weight: 600; font-size: 13px; color: #0f172a; margin-bottom: 6px; display: block; }
.fr-req { color: #dc2626; }
.fr-checks { display: flex; gap: 18px; }
.fr-checks label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; margin: 0; cursor: pointer; }
.fr-unit { max-width: 140px; }
.fr-warn { margin: 6px 0 0; font-size: 12px; color: #b45309; }
.fr-explain { margin-top: 18px; font-size: 12px; color: #64748b; line-height: 1.5; background: #f8fafc; border-radius: 6px; padding: 10px 12px; }

/* Form controls (copied from TxPresenter SettingsGeneral.vue) */
.sg-input,
.sg-input-num {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.sg-input:focus,
.sg-input-num:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.sg-input-num { max-width: 120px; }

/* Native select rendering was clipping the selected value (white-on-white inside the browser's
   own widget). Force a clean custom dropdown with our own chevron so the text always shows. */
select.sg-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 36px;
  line-height: 1.4;
  padding: 0 30px 0 10px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5l3 3 3-3'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  cursor: pointer;
}
.sg-input-row { display: flex; align-items: center; gap: 8px; }
.sg-hint { margin: 6px 0 0; font-size: 11.5px; color: #94a3b8; line-height: 1.5; }
</style>
